/**
 * Zayla Gold Value Card
 * Reads embedded gold price data and calculates the melt value of gold.
 * Dynamically updates when variant changes (14k vs 10k).
 *
 * Dependencies: None (vanilla JS)
 * Data source: #zayla-gold-data script tag (embedded JSON, updated daily)
 *              #zayla-variant-data script tag (variant info from Liquid)
 */
(function () {
  'use strict';

  var TROY_OZ_GRAMS = 31.1035;

  /**
   * Karat to purity mapping
   */
  var KARAT_MAP = {
    '24k': { purity: 1.000, label: '24k', desc: '24k gold is 99.9% pure gold.' },
    '18k': { purity: 0.750, label: '18k', desc: '18k gold is 75.0% pure gold.' },
    '14k': { purity: 0.583, label: '14k', desc: '14k gold is 58.3% pure gold.' },
    '10k': { purity: 0.417, label: '10k', desc: '10k gold is 41.7% pure gold.' }
  };

  /**
   * Detect karat from variant title or options
   * Falls back to defaultKarat if not detected in variant
   */
  function detectKarat(variantTitle, options, defaultKarat) {
    var text = (variantTitle + ' ' + (options || []).join(' ')).toLowerCase();
    if (text.indexOf('24k') !== -1) return '24k';
    if (text.indexOf('18k') !== -1) return '18k';
    if (text.indexOf('14k') !== -1) return '14k';
    if (text.indexOf('10k') !== -1) return '10k';
    // Fall back to default from Liquid (based on jewelry-material metafield)
    return defaultKarat || '14k';
  }

  /**
   * Read gold price from embedded JSON
   */
  function getGoldPrice() {
    var el = document.getElementById('zayla-gold-data');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent.trim());
    } catch (e) {
      console.warn('[Zayla Gold Value] Failed to parse gold price data:', e);
      return null;
    }
  }

  /**
   * Read variant data from embedded JSON
   */
  function getVariantData() {
    var el = document.getElementById('zayla-variant-data');
    if (!el) return [];
    try {
      return JSON.parse(el.textContent.trim());
    } catch (e) {
      console.warn('[Zayla Gold Value] Failed to parse variant data:', e);
      return [];
    }
  }

  /**
   * Calculate gold melt value
   */
  function calcMeltValue(weightGrams, pricePerOz, purity) {
    return weightGrams * (pricePerOz / TROY_OZ_GRAMS) * purity;
  }

  /**
   * Format USD
   */
  function formatUSD(amount) {
    return '$' + Math.round(amount).toLocaleString('en-US');
  }

  /**
   * Update the card with new values (used on init and variant change)
   */
  function updateCard(card, goldData, karat, variantPrice) {
    var weightGrams = parseFloat(card.dataset.gvWeight);
    var karatInfo = KARAT_MAP[karat] || KARAT_MAP['14k'];
    var purity = karatInfo.purity;
    var spotPrice = goldData.price;
    var productPrice = variantPrice || parseFloat(card.dataset.gvPrice);
    var meltValue = calcMeltValue(weightGrams, spotPrice, purity);
    var percentOfPrice = productPrice > 0 ? (meltValue / productPrice) * 100 : 0;
    var monthChange = goldData.change_pct_month || 0;

    var spotEl = card.querySelector('.gv-spot-price');
    var valueEl = card.querySelector('.gv-melt-value');
    var weightEl = card.querySelector('.gv-weight-text');
    var trendEl = card.querySelector('.gv-trend');
    var barEl = card.querySelector('.gv-bar-fill');
    var pctEl = card.querySelector('.gv-percent');
    var mathEl = card.querySelector('.gv-math');
    var karatLabels = card.querySelectorAll('.gv-karat-label, .gv-karat-text');
    var purityText = card.querySelector('.gv-purity-text');

    if (spotEl) spotEl.textContent = 'Gold at ' + formatUSD(spotPrice) + '/oz';
    if (valueEl) valueEl.textContent = formatUSD(meltValue);
    if (weightEl) weightEl.textContent = 'Approx. ' + weightGrams.toFixed(1) + 'g of solid ' + karatInfo.label + ' gold';

    karatLabels.forEach(function (el) {
      el.textContent = karatInfo.label;
    });
    if (purityText) purityText.textContent = karatInfo.desc;

    if (trendEl) {
      if (monthChange > 0) {
        trendEl.textContent = 'Gold price \u25B2 ' + Math.abs(monthChange).toFixed(1) + '% this month';
        trendEl.classList.remove('down');
      } else if (monthChange < 0) {
        trendEl.textContent = 'Gold price \u25BC ' + Math.abs(monthChange).toFixed(1) + '% this month';
        trendEl.classList.add('down');
      } else {
        trendEl.textContent = 'Gold price unchanged this month';
      }
    }

    if (barEl) {
      barEl.style.width = Math.min(percentOfPrice, 100).toFixed(1) + '%';
    }

    if (pctEl) {
      pctEl.textContent = percentOfPrice.toFixed(0) + '% of the price is gold alone';
    }

    // Investment / 1-year comparison
    var investEl = card.querySelector('.gv-investment-text');
    if (investEl && goldData.price_1yr_ago) {
      var meltValueYearAgo = calcMeltValue(weightGrams, goldData.price_1yr_ago, purity);
      investEl.innerHTML = '\uD83D\uDCC8 One year ago today, the gold in this piece was worth ' +
        formatUSD(meltValueYearAgo) + '. Today it\u2019s worth ' + formatUSD(meltValue) + '.';
    }

    if (mathEl) {
      mathEl.innerHTML =
        weightGrams.toFixed(1) + 'g &times; (' + formatUSD(spotPrice) +
        '/oz &divide; 31.1 g/oz) &times; ' + (purity * 100).toFixed(1) +
        '% purity = <strong>' + formatUSD(meltValue) + ' in gold</strong>';
    }
  }

  /**
   * Get the currently selected variant ID from the page.
   * 
   * IMPORTANT: Broadcast 8.1 has multiple forms with name="id" inputs:
   *   1. Upsell form (ProductFormUpsell)
   *   2. Installment form (product-form-installment)
   *   3. Main product form (product-form-template with .product-variant-id)
   * 
   * We need to target the MAIN product form specifically, not the first match.
   * Falls back to URL param if DOM selector fails.
   */
  function getCurrentVariantId() {
    // Broadcast 8.1 specific: main product form uses .product-variant-id class
    var input = document.querySelector(
      'input.product-variant-id[name="id"], ' +
      'form[data-type="add-to-cart-form"] input[name="id"], ' +
      'form[id^="product-form-template"] input[name="id"]'
    );
    if (input && input.value) return parseInt(input.value, 10);

    // Fallback: check URL param (updated by Broadcast on variant selection)
    var urlParams = new URLSearchParams(window.location.search);
    var variantParam = urlParams.get('variant');
    if (variantParam) return parseInt(variantParam, 10);

    // Last resort: generic selector (may grab wrong form but better than nothing)
    var genericInput = document.querySelector('input[name="id"][type="hidden"], select[name="id"]');
    if (genericInput && genericInput.value) return parseInt(genericInput.value, 10);

    return null;
  }

  /**
   * Initialize a gold value card element
   */
  function initCard(card) {
    var weightGrams = parseFloat(card.dataset.gvWeight);
    if (!weightGrams || weightGrams <= 0) {
      card.style.display = 'none';
      return;
    }

    var goldData = getGoldPrice();
    if (!goldData || !goldData.price) {
      card.style.display = 'none';
      return;
    }

    var variants = getVariantData();
    var currentVariantId = getCurrentVariantId();
    
    // Get default karat from Liquid (based on jewelry-material metafield)
    var defaultKarat = card.dataset.gvDefaultKarat || '14k';

    var karat = defaultKarat;
    var variantPrice = null;
    if (currentVariantId && variants.length > 0) {
      for (var i = 0; i < variants.length; i++) {
        if (variants[i].id === currentVariantId) {
          karat = detectKarat(variants[i].title, variants[i].options, defaultKarat);
          variantPrice = variants[i].price;
          break;
        }
      }
    } else if (variants.length > 0) {
      karat = detectKarat(variants[0].title, variants[0].options, defaultKarat);
      variantPrice = variants[0].price;
    }

    updateCard(card, goldData, karat, variantPrice);
    card.classList.add('is-visible');

    // Explainer toggle
    var toggle = card.querySelector('.gv-explainer-toggle');
    var explainer = card.querySelector('.gv-explainer');
    if (toggle && explainer) {
      toggle.addEventListener('click', function () {
        explainer.classList.toggle('is-open');
        toggle.textContent = explainer.classList.contains('is-open')
          ? 'Hide calculation'
          : 'How is this calculated?';
      });
    }

    // === Listen for variant changes ===
    var lastVariantId = currentVariantId;
    function checkVariantChange() {
      var newId = getCurrentVariantId();
      if (newId && newId !== lastVariantId) {
        lastVariantId = newId;
        for (var j = 0; j < variants.length; j++) {
          if (variants[j].id === newId) {
            var newKarat = detectKarat(variants[j].title, variants[j].options, defaultKarat);
            updateCard(card, goldData, newKarat, variants[j].price);
            break;
          }
        }
      }
    }

    setInterval(checkVariantChange, 200);

    // Listen on main product form for change events
    var productForm = document.querySelector(
      'form[data-type="add-to-cart-form"], ' +
      'form[id^="product-form-template"], ' +
      'form[action*="/cart/add"], ' +
      '.product-form, [data-product-form]'
    );
    if (productForm) {
      productForm.addEventListener('change', function () {
        setTimeout(checkVariantChange, 50);
      });
    }

    document.addEventListener('shopify:section:load', function () {
      setTimeout(checkVariantChange, 100);
    });

    // Broadcast 8.1: Subscribe to pub/sub variant change events if available
    // This fires BEFORE the DOM updates, giving us instant response
    if (typeof window.theme !== 'undefined' && window.theme.PUB_SUB_EVENTS && typeof subscribe === 'function') {
      try {
        subscribe(theme.PUB_SUB_EVENTS.optionValueSelectionChange, function () {
          setTimeout(checkVariantChange, 100); // Small delay for DOM to update
        });
      } catch (e) {
        // Pub/sub not available, polling will handle it
      }
    }

    // MutationObserver on main variant input (Broadcast uses .product-variant-id)
    // NOTE: MutationObserver only catches attribute changes, not property changes.
    // Broadcast updates .value property directly, so this mainly helps other themes.
    var variantInput = document.querySelector(
      'input.product-variant-id[name="id"], ' +
      'form[data-type="add-to-cart-form"] input[name="id"]'
    );
    if (variantInput) {
      var observer = new MutationObserver(function () {
        setTimeout(checkVariantChange, 50);
      });
      observer.observe(variantInput, { attributes: true, attributeFilter: ['value'] });
    }
  }

  function init() {
    var cards = document.querySelectorAll('.gold-value-card');
    cards.forEach(initCard);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
